<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import {
  eNumeroDeProjeto,
  eEmailDeServiceAccount,
} from './googleChat/validacao';

import PageHeader from '../../SettingsSubPageHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Caixa do Google Chat (GCHAT-01). Só dados que não são segredo: o número do projeto GCP do
// app de Chat e o e-mail da service account do cliente. A credencial que envia é a da
// Powerbizz, que o cliente autoriza a agir por essa service account (passo 4 da tela final).
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const uiFlags = useMapGetter('inboxes/getUIFlags');

const state = reactive({
  channelName: '',
  projectNumber: '',
  serviceAccountEmail: '',
});

const rules = {
  channelName: { required },
  projectNumber: { required, eNumeroDeProjeto },
  serviceAccountEmail: { required, eEmailDeServiceAccount },
};

const v$ = useVuelidate(rules, state);

// O erro vem do próprio Input; a ajuda fica fora dele, embaixo, porque o Input corta a mensagem
// numa linha e a ajuda da service account não cabe em uma. E quem preenche precisa saber ONDE
// achar o número e QUAL service account é mesmo antes de errar.
const erroDe = (nome, texto) =>
  computed(() => (v$.value[nome].$error ? texto() : ''));

const erroDoNome = erroDe('channelName', () =>
  t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.CHANNEL_NAME.ERROR')
);
const erroDoProjeto = erroDe('projectNumber', () =>
  t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.PROJECT_NUMBER.ERROR')
);
const erroDaServiceAccount = erroDe('serviceAccountEmail', () =>
  t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.SERVICE_ACCOUNT_EMAIL.ERROR')
);

// O `throwErrorMessage` da store embrulha a resposta num Error: com mensagem do servidor
// (validação do canal), ela diz o que corrigir; sem, o texto é o do axios e não serve a ninguém.
const motivoDoServidor = error => {
  const motivo = error?.message;
  return motivo && !/^(Axios)?Error\b/.test(motivo) ? motivo : '';
};

async function createChannel() {
  const valido = await v$.value.$validate();
  if (!valido) return;

  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: state.channelName.trim(),
      channel: {
        type: 'google_chat',
        project_number: state.projectNumber.trim(),
        service_account_email: state.serviceAccountEmail.trim(),
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: inbox.id },
    });
  } catch (error) {
    const falha = t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.API.ERROR_MESSAGE');
    const motivo = motivoDoServidor(error);
    useAlert(motivo ? `${falha}: ${motivo}` : falha);
  }
}
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.DESC')"
    />

    <!-- `novalidate`: o e-mail é `type="email"` pelo teclado no celular, mas quem valida é o
         formulário, com a mensagem dele, e não o balão do navegador. -->
    <form
      class="flex flex-col flex-wrap gap-4 mx-0"
      novalidate
      @submit.prevent="createChannel"
    >
      <Input
        v-model="state.channelName"
        :label="t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.CHANNEL_NAME.LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
        "
        :message="erroDoNome"
        :message-type="erroDoNome ? 'error' : 'info'"
        @blur="v$.channelName.$touch"
      />

      <div class="flex flex-col gap-1">
        <Input
          v-model="state.projectNumber"
          inputmode="numeric"
          :label="t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.PROJECT_NUMBER.LABEL')"
          :placeholder="
            t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.PROJECT_NUMBER.PLACEHOLDER')
          "
          :message="erroDoProjeto"
          :message-type="erroDoProjeto ? 'error' : 'info'"
          @blur="v$.projectNumber.$touch"
        />
        <p class="mb-0 text-label-small text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.PROJECT_NUMBER.HELP') }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <Input
          v-model="state.serviceAccountEmail"
          type="email"
          :label="
            t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.SERVICE_ACCOUNT_EMAIL.LABEL')
          "
          :placeholder="
            t(
              'INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.SERVICE_ACCOUNT_EMAIL.PLACEHOLDER'
            )
          "
          :message="erroDaServiceAccount"
          :message-type="erroDaServiceAccount ? 'error' : 'info'"
          @blur="v$.serviceAccountEmail.$touch"
        />
        <p class="mb-0 text-label-small text-n-slate-11">
          {{
            t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.SERVICE_ACCOUNT_EMAIL.HELP')
          }}
        </p>
      </div>

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          :label="t('INBOX_MGMT.ADD.GOOGLE_CHAT_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
